///
/// EarthBrowser
///
/// Created by Matt Giger
/// Copyright (c) 2025 Geodata Labs
///

import { NodeBase } from "@stovokor/engine/NodeBase"
import { RenderState } from "@stovokor/engine/RenderState"
import { StovokorContext } from "@stovokor/StovokorContext"
import { FocusTarget } from "./FocusTarget"
import type { AnyGestureRecognizer, GestureHitResult } from "./GestureRecognizer"

/**
 * Base interface for nodes that can hold gesture recognizers
 *
 * This is the minimal interface for gesture recognition - nodes that
 * can attach recognizers and participate in gesture handling.
 */
export interface GestureRecognizerHolder {
	readonly recognizers: AnyGestureRecognizer[]
	addRecognizer(recognizer: AnyGestureRecognizer): void
	removeRecognizer(recognizer?: AnyGestureRecognizer): void
}

/**
 * Full gesture target interface for 3D scene nodes
 *
 * Extends the base recognizer holder with 3D ray-based hit testing.
 * Used by GestureNode and WorldViewport for 3D gesture detection.
 */
export interface GestureTarget extends GestureRecognizerHolder {
	gestureHitTest(state: RenderState): GestureHitResult[]
}

/**
 * GestureNode - Base class for gesture-aware scene nodes
 *
 * Extends NodeBase to provide both scene graph functionality and gesture handling.
 * This is the recommended way to create gesture-aware nodes.
 *
 * Features inherited from NodeBase:
 * - Transform hierarchy (position, rotation, scale)
 * - Child node management
 * - Hit testing via geometry bounds
 * - Render action generation
 *
 * Features added for gestures:
 * - Gesture recognizer management
 * - Focus request handling
 * - Default implementations of GestureNode interface
 */
export abstract class GestureNode extends NodeBase implements FocusTarget, GestureTarget {
	/** Unique identifier - must be implemented by subclass */
	abstract readonly id: string
	abstract readonly focusable: boolean

	protected context: StovokorContext
	private _recognizers: AnyGestureRecognizer[] = []

	constructor(context: StovokorContext) {
		super()
		this.context = context
	}

	public get recognizers(): AnyGestureRecognizer[] {
		return this._recognizers
	}

	public addRecognizer(recognizer: AnyGestureRecognizer): void {
		recognizer.owner = this
		this._recognizers.push(recognizer)
	}

	public removeRecognizer(recognizer?: AnyGestureRecognizer): void {
		if(!recognizer) return

		const index = this._recognizers.indexOf(recognizer)
		if (index !== -1) {
			this._recognizers[index].owner = undefined
			this._recognizers.splice(index, 1)
		}
	}

	// ========================= Focus Target Implementation =========================

	public requestFocus(): boolean {
		if (!this.focusable) return false
		return this.context.focusManager.requestFocus(this) ?? false
	}

	// Default implementation - override as needed
	public shouldRelinquishFocus(_requester: FocusTarget): boolean {
		return true // Allow by default
	}
}
